import { Box, Step, StepLabel, Stepper } from '@mui/material';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { BasicDialog } from '../../../components/Dialog/BasicDialog';
import { buildFormatterFormField } from '../../../components/FormatterSelect/FormatterSelect';
import { formatterModelsByTypeAtom } from '../../../components/FormatterSelect/formatter-options-state';
import { ErrorMessage, StepperBox, StepperSx, StepSx } from '../../../components/Form/FormComponents';
import { scalarType, validateFormula } from '../../../components/FormulaEditor/validateFormula';
import { inferResultType, parse } from '../../../formula-engine';
import { RendererType } from '../../../data-models/field-definition';
import { PrimitiveType } from '../../../data-models/field3.data-model';
import { IFormatterDataModel } from '../../../data-models/formatter.data-model';
import {
  FORMULA_FIELD_ENTITIES,
  FormulaFieldEntity,
  FormulaFieldType,
  IFormulaFieldDataModel,
  normalizeFormulaFieldType,
} from '../../../data-models/formula-field.data-model';
import {
  createFormulaField,
  deleteFormulaField,
  fetchFormulaFields,
  updateFormulaField,
} from '../../../services/queries/MaggieFormulaFieldQueries';
import {
  columnsRawLoadable,
  columnsRawUnwrapped,
} from '../../../services/state/AdminPanel/ColumnDefinitionsState';
import {
  displayedFormulaFieldIdsAtom,
  displayedFormulaFieldIdsLoadable,
  formulaFieldsListAtom,
  formulaFieldsListLoadable,
} from '../../../services/state/AdminPanel/FormulaFieldsStateJ';
import {
  COMPANY_VIEWS_COMPONENT,
  getDisplayedFormulaFieldIds,
  updateFormulaDisplayConfig,
} from '../../../services/queries/FieldDisplayConfigQueries';
import { kpiConfigLoadable, quantitativeKpiConfigUnwrapped } from '../../../services/state/KPIConfigState';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import { getErrorMessage } from '../../../services/queryHelpers';
import { MaggieFeatureFlags } from '../../../util/feature-flags';
import { DEFAULT_FORMULA_FORMAT, displayNameToKey, findFormulaCycle } from '../../../util/formula-fields';
import { buildAllFormulaVocabularies, FormulaVocabulary } from '../../../util/formula-vocabulary';
import { ConfigFormFields, getInitialValues } from '../../Config/Common/ConfigModal';
import {
  createFormField,
  createFormFromFieldsArray,
  IFormField,
  IFormFieldFormulaMeta,
} from '../../../view-models/form.view-model';
import { StepperFormButtons } from '../../Finance2/Forms/StepperFormButtons';
import { FormulaFieldDisplayStep } from './FormulaFieldDisplayStep';
import { formulaFieldFormStepState, useResetFormulaFieldFormState } from './FormulaFieldFormStateJ';

const STEP_LABELS = ['Field Settings', 'Set Field Display'];

interface FormulaFieldModalProps {
  item?: IFormulaFieldDataModel;
  onClose: () => void;
}

interface FormulaFieldModalInnerProps extends FormulaFieldModalProps {
  showInGrid: boolean;
  onShowInGridChange: (showInGrid: boolean) => void;
}

type FormulaFieldFormData = Pick<
  IFormulaFieldDataModel,
  'displayName' | 'description' | 'entity' | 'format' | 'formula' | 'type'
> & { key?: string };

// Same labels the Custom Field form gives these entities (FieldEntityOptions).
export const FORMULA_ENTITY_LABEL: Record<FormulaFieldEntity, string> = {
  company: 'Company',
  round: 'Round',
  captableInvestment: 'Security',
};

const TYPE_LABEL: Record<FormulaFieldType, string> = {
  number: 'Number',
  string: 'Text',
  boolean: 'Yes/No',
  date: 'Date',
};

/** "This formula returns text — set Type to Text", for each type a formula can have. */
const RESULT_TYPE_HINT: Record<FormulaFieldType, string> = {
  number: 'This formula returns a number — set Type to Number',
  string: 'This formula returns text — set Type to Text',
  boolean: 'This formula returns TRUE/FALSE — set Type to Yes/No',
  date: 'This formula returns a date — set Type to Date',
};

/**
 * The Format field: the shared formatter picker, scoped to what the formula evaluates to.
 * `FormulaFieldType` is already a `PrimitiveType`, so the family needs no mapping.
 */
function getFormatFormField(
  formatterModelsByType: Map<PrimitiveType, IFormatterDataModel<unknown>[]>,
  fieldType: FormulaFieldType,
  value: string | undefined
) {
  return buildFormatterFormField({
    formatterModelsByType,
    key: 'format',
    label: 'Format',
    type: fieldType,
    value,
  });
}

/** Rules close over the vocabularies and formula-field list, so the fields are built per instance. */
function getFormulaFormFields(
  vocabularies: Record<FormulaFieldEntity, FormulaVocabulary>,
  formulaFields: IFormulaFieldDataModel[],
  item: IFormulaFieldDataModel | undefined,
  formatField: IFormField<unknown> | undefined,
  showSecurityView: boolean
): IFormField<unknown>[] {
  const isEdit = item != null;
  const vocabularyFor = (formValues: FieldValues): FormulaVocabulary =>
    vocabularies[(formValues.entity as FormulaFieldEntity) ?? 'company'];
  // Like the Custom Field form, Security is only offered where the Security
  // view exists; an existing captableInvestment field keeps showing its
  // (locked) entity so edits still render it.
  const entityValues = FORMULA_FIELD_ENTITIES.filter(
    (value) => value !== 'captableInvestment' || showSecurityView || item?.entity === 'captableInvestment'
  );
  const fields: IFormField<unknown>[] = [
    createFormField({
      key: 'entity',
      label: 'Entity',
      description:
        'What the formula attaches to; decides the referenceable fields and the views the column appears on. Fixed after creation because the formula references its fields.',
      renderer: RendererType.singleSelect,
      required: true,
      disabled: isEdit,
      fullWidth: true,
      rendererMeta: {
        values: entityValues.map((value) => ({
          value,
          displayName: FORMULA_ENTITY_LABEL[value],
        })),
      },
    }),
    createFormField({
      key: 'displayName',
      label: 'Display Name',
      renderer: RendererType.text,
      required: true,
      autoFocus: true,
      fullWidth: true,
    }),
  ];
  if (isEdit) {
    fields.push(
      createFormField({
        key: 'key',
        label: 'Key',
        description: 'Generated from the display name at creation; fixed because formulas reference it.',
        renderer: RendererType.text,
        disabled: true,
        fullWidth: true,
      })
    );
  }
  fields.push(
    createFormField({
      key: 'description',
      label: 'Description',
      renderer: RendererType.text,
      fullWidth: true,
    }),
    createFormField({
      key: 'type',
      label: 'Type',
      description: 'What the formula evaluates to; drives the available formats.',
      renderer: RendererType.singleSelect,
      required: true,
      fullWidth: true,
      rendererMeta: {
        values: (Object.keys(TYPE_LABEL) as FormulaFieldType[]).map((value) => ({
          value,
          displayName: TYPE_LABEL[value],
        })),
      },
    })
  );
  if (formatField) {
    fields.push(formatField);
  }
  fields.push(
    createFormField({
      key: 'formula',
      label: 'Formula',
      renderer: RendererType.formula,
      required: true,
      fullWidth: true,
      // The renderer builds its own vocabulary; it must exclude this field too.
      rendererMeta: { excludeKey: item?.key } satisfies IFormFieldFormulaMeta,
      rules: {
        // Submit-gating on the canonical value; FieldFormula renders the rich
        // (display-text, ranged) errors itself.
        validate: (value: unknown, formValues: FieldValues) => {
          const vocabulary = vocabularyFor(formValues);
          const canonical = (value as string) ?? '';
          const validation = validateFormula({ display: canonical, canonical }, vocabulary);
          if (validation.state === 'error') {
            return validation.message;
          }
          if (validation.state === 'ok') {
            const resultType = inferResultType(parse(validation.canonical), (ref) =>
              scalarType(vocabulary.byPath.get(ref)?.type ?? '')
            );
            if (resultType !== normalizeFormulaFieldType(formValues.type)) {
              return RESULT_TYPE_HINT[resultType];
            }
            const displayName = ((formValues.displayName as string) ?? '').trim();
            // On create, the key this save will generate — a recreated key can
            // close a cycle through formulas that referenced its predecessor.
            const key = item?.key ?? displayNameToKey(displayName);
            const cycle = findFormulaCycle(
              { key, displayName: displayName || key, formula: validation.canonical },
              formulaFields
            );
            if (cycle) {
              return `This formula creates a circular reference: ${cycle.map((name) => `[${name}]`).join(' → ')}`;
            }
          }
          return true;
        },
      },
    })
  );
  return fields;
}

/**
 * The formula editor builds chips (labelled with display paths) from the
 * stored canonical formula when it mounts, so an edit must wait for the full
 * vocabulary — columns and the other formula fields (a stored formula may
 * reference them); a create has nothing to segment and can open immediately.
 */
export function FormulaFieldModal({ item, onClose }: FormulaFieldModalProps) {
  const columnsLoad = useAtomValue(columnsRawLoadable);
  const formulaLoad = useAtomValue(formulaFieldsListLoadable);
  const displayedLoad = useAtomValue(displayedFormulaFieldIdsLoadable);
  const kpiLoad = useAtomValue(kpiConfigLoadable);
  const resetFormState = useResetFormulaFieldFormState();
  // Both live on the gate, not below it: the inner component unmounts whenever
  // an atom refreshes, so a step reset or a toggle held down there would be
  // silently discarded mid-edit — and stepping back to 1 unmounts the display
  // step itself, which would otherwise re-seed the toggle from the stored value.
  // A new field starts displayed; an existing one reflects its registration.
  const [showInGrid, setShowInGrid] = useState<boolean | null>(item ? null : true);

  useEffect(() => {
    return resetFormState;
  }, [resetFormState]);

  const registeredIds = displayedLoad.state === 'hasData' ? displayedLoad.data : null;
  useEffect(() => {
    if (item && registeredIds) {
      setShowInGrid((current) => current ?? registeredIds.has(item.id));
    }
  }, [item, registeredIds]);

  if (
    item &&
    (columnsLoad.state === 'loading' ||
      formulaLoad.state === 'loading' ||
      displayedLoad.state === 'loading' ||
      kpiLoad.state === 'loading')
  ) {
    return null;
  }
  return (
    <FormulaFieldModalInner
      item={item}
      onClose={onClose}
      showInGrid={showInGrid ?? true}
      onShowInGridChange={setShowInGrid}
    />
  );
}

/**
 * Two-step formula field create/edit flow (mirrors KpiFieldForm): step 1 is the
 * schema-driven form, step 2 sets the field's display (showInGrid). The field
 * itself is only saved on the final step.
 */
function FormulaFieldModalInner({
  item,
  onClose,
  showInGrid,
  onShowInGridChange,
}: FormulaFieldModalInnerProps) {
  const columnsMeta = useAtomValue(columnsRawUnwrapped);
  const formatterModelsByType = useAtomValue(formatterModelsByTypeAtom);
  const setFormulaFieldsList = useSetAtom(formulaFieldsListAtom);
  const setDisplayedIds = useSetAtom(displayedFormulaFieldIdsAtom);
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();
  const isEdit = item != null;
  const [step, setStep] = useAtom(formulaFieldFormStepState);

  const formulaLoad = useAtomValue(formulaFieldsListLoadable);
  const formulaFields = useMemo(
    () => (formulaLoad.state === 'hasData' ? (formulaLoad.data ?? []) : []),
    [formulaLoad]
  );
  const kpis = useAtomValue(quantitativeKpiConfigUnwrapped);
  const vocabularies = useMemo(
    () => buildAllFormulaVocabularies(columnsMeta, formulaFields, { excludeKey: item?.key, kpis }),
    [columnsMeta, formulaFields, item?.key, kpis]
  );
  const { showSecurityView } = useFlags<MaggieFeatureFlags>();
  const initialType: FormulaFieldType = normalizeFormulaFieldType(item?.type);
  // Stable across edits: useForm's `values` resets form state whenever it
  // changes, so only the displayed form (via dynamicFormTransformer below)
  // may react to the Type selection.
  const initialFormat = item?.format ?? DEFAULT_FORMULA_FORMAT[initialType];
  const form = useMemo(
    () =>
      createFormFromFieldsArray<FormulaFieldFormData>(
        getFormulaFormFields(
          vocabularies,
          formulaFields,
          item,
          getFormatFormField(formatterModelsByType, initialType, initialFormat),
          showSecurityView
        ),
        {
          data: {
            displayName: item?.displayName ?? '',
            key: item?.key,
            description: item?.description ?? '',
            entity: item?.entity ?? 'company',
            type: initialType,
            format: initialFormat,
            formula: item?.formula ?? '',
          },
        }
      ),
    [vocabularies, formulaFields, initialType, initialFormat, item, showSecurityView, formatterModelsByType]
  );
  const formMethods = useForm({ values: getInitialValues(form) });
  const watchedType: FormulaFieldType = normalizeFormulaFieldType(formMethods.watch('type'));
  // Watched, not read: the picker annotates its input with the selected formatter's sample,
  // so the field has to be rebuilt when the choice changes.
  const watchedFormat = formMethods.watch('format') as string | undefined;

  // Re-derive the displayed fields when Type changes (Format options differ);
  // a new transformer identity is what makes ConfigFormFields re-run it.
  const dynamicFormTransformer = useCallback(
    () =>
      createFormFromFieldsArray<FormulaFieldFormData>(
        getFormulaFormFields(
          vocabularies,
          formulaFields,
          item,
          getFormatFormField(formatterModelsByType, watchedType, watchedFormat),
          showSecurityView
        ),
        { data: form.data }
      ),
    [
      vocabularies,
      formulaFields,
      item,
      watchedType,
      watchedFormat,
      form,
      showSecurityView,
      formatterModelsByType,
    ]
  );

  // A type switch invalidates the other type's formats; snap to the default. Sourced from the
  // shared registry, so a format the tenant does not offer for this type snaps too rather than
  // leaving the picker showing a value that is not in its list.
  const allowedFormats = useMemo(
    () => new Set((formatterModelsByType.get(watchedType) ?? []).map((model) => model.id)),
    [formatterModelsByType, watchedType]
  );
  useEffect(() => {
    if (!allowedFormats.has(formMethods.getValues('format') as string)) {
      formMethods.setValue('format', DEFAULT_FORMULA_FORMAT[watchedType]);
    }
  }, [allowedFormats, watchedType, formMethods]);
  const { errors } = formMethods.formState;
  const errorCount = Object.keys(errors).length;
  // Delete bypasses the stepper's Next; its own flag keeps Next disabled meanwhile.
  const [isDeleting, setIsDeleting] = useState(false);

  const step1IsValid = useCallback(
    () =>
      new Promise<boolean>((resolve) => {
        void formMethods.handleSubmit(
          () => resolve(true),
          () => resolve(false)
        )();
      }),
    [formMethods]
  );

  const saveField = async (): Promise<boolean> => {
    const data = formMethods.getValues();
    const entity = (data.entity as FormulaFieldEntity) ?? 'company';
    const canonical = (data.formula as string) ?? '';
    const validation = validateFormula({ display: canonical, canonical }, vocabularies[entity]);
    if (validation.state !== 'ok') {
      // Step 2 has no error surface, and a formula that is merely blank passes
      // step 1's rule — without this the final button would just spin.
      pushErrorToast({ message: 'The formula is not valid. Check it on the previous step.' });
      setStep(0);
      return false;
    }
    const fieldType: FormulaFieldType = normalizeFormulaFieldType(data.type);
    const payload = {
      key: item?.key ?? displayNameToKey(data.displayName as string),
      displayName: (data.displayName as string).trim(),
      description: (data.description as string) ?? '',
      entity,
      type: fieldType,
      format: data.format as string,
      // The normalized print(ast) — chip refs and hand-typed names both
      // resolved to canonical row paths.
      formula: validation.canonical,
    };
    // The field first: its id is what the display registration is keyed on.
    let saved: IFormulaFieldDataModel;
    try {
      saved = item
        ? await updateFormulaField({ id: item.id, ...payload })
        : await createFormulaField(payload);
    } catch (err) {
      pushErrorToast({ message: getErrorMessage(err, isEdit ? 'Update failed' : 'Create failed') });
      return false;
    }
    try {
      await updateFormulaDisplayConfig(saved.id, {
        component: COMPANY_VIEWS_COMPONENT,
        displayed: showInGrid,
      });
      pushSuccessToast({ message: isEdit ? 'Formula field updated.' : 'Formula field created.' });
    } catch (err) {
      // The field is saved either way; only its display registration is behind.
      pushErrorToast({
        message: getErrorMessage(
          err,
          'Field saved, but updating its display failed. Edit the field to retry.'
        ),
      });
    }
    setFormulaFieldsList(fetchFormulaFields());
    setDisplayedIds(getDisplayedFormulaFieldIds());
    onClose();
    return true;
  };

  const onDelete = async () => {
    if (!item) {
      return;
    }
    setIsDeleting(true);
    try {
      await deleteFormulaField(item);
      setFormulaFieldsList(fetchFormulaFields());
      // The backend cascades the registration away with the field.
      setDisplayedIds(getDisplayedFormulaFieldIds());
      onClose();
    } catch (err) {
      pushErrorToast({ message: getErrorMessage(err, 'Delete failed') });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <BasicDialog open title={isEdit ? 'Edit Formula Field' : 'Create Formula Field'} onClose={onClose}>
      <FormProvider {...formMethods}>
        {/* Same width as the modal had pre-stepper, and as KpiFieldForm. */}
        <Box sx={{ width: '50vw' }}>
          <StepperBox>
            <Stepper activeStep={step} sx={StepperSx}>
              {STEP_LABELS.map((label) => (
                <Step key={label} sx={StepSx}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            {step === 0 ? (
              <>
                <ConfigFormFields form={form} dynamicFormTransformer={dynamicFormTransformer} />
                {errorCount > 0 && (
                  <ErrorMessage>{errorCount} error(s) in this form, please review.</ErrorMessage>
                )}
                <StepperFormButtons
                  stepState={formulaFieldFormStepState}
                  stepIsValid={step1IsValid}
                  handleGoBack={onClose}
                  backButtonLabel='Cancel'
                  nextDisabled={isDeleting}
                  secondaryActionProps={
                    isEdit
                      ? {
                          label: 'Delete',
                          handler: onDelete,
                          buttonProps: { color: 'error', variant: 'outlined' },
                        }
                      : undefined
                  }
                />
              </>
            ) : (
              <FormulaFieldDisplayStep
                isEdit={isEdit}
                showInGrid={showInGrid}
                onShowInGridChange={onShowInGridChange}
                onSave={saveField}
              />
            )}
          </StepperBox>
        </Box>
      </FormProvider>
    </BasicDialog>
  );
}
